import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getMyProfile, updateMyProfile } from '@/api/profile';
import { getMySales } from '@/api/sales';
import { getMyPurchases } from '@/api/purchase';
import { getWatches } from '@/api/watches';
import useAuthStore from '@/stores/authStore';
import ProfileItemCard from '@/components/ProfileItemCard';
import CardItemView from '@/components/CardItemView';
import ProfileEditor from '@/components/ProfileEditor';
import { formatCurrency } from '@/utils/formatUtils';
import { calculateTimeLeft } from '@/utils/countdownUtils';
import { getWatchTypeDisplay } from '@/utils/watchUtils';
import { getSaleStatusTranslationKey, getSaleMessageTitleKey } from '@/config/saleStatus';
import { getOrderStatusTranslationKey, getOrderMessageTitleKey } from '@/config/orderStatus';
import avatar from '@/assets/avatar.svg';
import { getAvatarThumbnailUrl } from '@/utils/imageUtils';
import sellIcon from '@/assets/sell.svg';
import orderIcon from '@/assets/order.svg';
import favIcon from '@/assets/heart.svg';
import dondonGreyLogo from '@/assets/dondon_grey.svg';
import PenaltyNotice from '@/components/PenaltyNotice';
import usePenaltyStatus from '@/hooks/usePenaltyStatus';
import { openPenaltyPaymentModal } from '@/stores/penaltyPaymentStore';



const ProfileInfoPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const [isProfileEditorOpen, setIsProfileEditorOpen] = useState(false);
  const { hasOutstandingPenalties, isBanned } = usePenaltyStatus();
  const handlePenaltyAction = useCallback(() => {
    openPenaltyPaymentModal();
  }, []);

  // Fetch profile data
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getMyProfile
  });

  // Mutation for updating profile
  const updateProfileMutation = useMutation({
    mutationFn: updateMyProfile,
    onSuccess: () => {
      // Invalidate and refetch the profile query to get updated data
      queryClient.invalidateQueries(['user-profile']);
      setIsProfileEditorOpen(false);
    },
    onError: (error) => {
      console.error('Failed to update profile:', error);
    }
  });

  // Fetch recent sales - limit to 2 most recent
  const { data: salesData, isLoading: isLoadingSales } = useQuery({
    queryKey: ['sales', 'ALL'],
    queryFn: () => getMySales(),
    select: (data) => {
      // Sort by createdAt and take first 2
      const sortedData = [...(data.data || [])].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      ).slice(0, 2);
      
      // Calculate shippingTimeLeft for each sale
      sortedData.forEach(sale => {
        if (sale.status === 'PAID' && sale.sellerShippingDue) {
          sale.shippingTimeLeft = calculateTimeLeft(sale.sellerShippingDue, t);
        }
      });
      
      return {
        ...data,
        data: sortedData
      };
    }
  });

  // Fetch recent purchases - limit to 2 most recent
  const { data: purchasesData, isLoading: isLoadingPurchases } = useQuery({
    queryKey: ['purchases', 'ALL'],
    queryFn: () => getMyPurchases(),
    select: (data) => {
      // Sort by createdAt and take first 2
      return {
        ...data,
        data: [...(data.data || [])].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        ).slice(0, 2)
      };
    }
  });

  // Fetch favorites/watches
  const { data: watchesData, isLoading: isLoadingFavorites } = useQuery({
    queryKey: ['watches'],
    queryFn: () => getWatches(null),
    select: (data) => {
      // Take only first 4 watches
      return {
        ...data,
        data: (data.data || []).slice(0, 4)
      };
    }
  });

  // Extract data
  const profileInfo = profileData?.data || user || {
    id: '',
    nickname: '',
    bio: t('me.profile.info.earnings'),
    avatar: null,
    verified: false
  };
  const stats = {
    favoriteCount: profileInfo?.watchStats?.itemCount || watchesData?.data?.length || 0,
    listingCount: profileInfo?.sellerStats?.onSaleCount || 0,
    soldCount: profileInfo?.sellerStats?.completedCount || profileInfo?.soldCount || 0,
    purchasedCount: profileInfo?.buyerStats?.completedCount || profileInfo?.purchasedCount || 0,
    pendingSellerShipmentCount: profileInfo?.buyerStats?.pendingShipmentCount || 0,
    pendingReceiptCount: profileInfo?.buyerStats?.shippedCount || 0,
    pendingShipmentCount: profileInfo?.sellerStats?.pendingShipmentCount || 0,
    balance: profileInfo?.sellerStats?.balance || 0,
    totalEarnings: profileInfo?.sellerStats?.totalEarnings || 0,
    shippedCount: profileInfo?.sellerStats?.shippedCount || 0,
    cancelledSalesCount: profileInfo?.sellerStats?.cancelledCount || 0,
    cancelledPurchasesCount: profileInfo?.buyerStats?.cancelledCount || 0
  };

  const recentSales = salesData?.data || [];
  const recentPurchases = purchasesData?.data || [];
  const recentFavorites = watchesData?.data || [];

  const hasSales = recentSales.length > 0;
  const hasPurchases = recentPurchases.length > 0;
  const hasFavorites = recentFavorites.length > 0;

  const handleOpenProfileEditor = () => {
    setIsProfileEditorOpen(true);
  };

  const handleCloseProfileEditor = () => {
    setIsProfileEditorOpen(false);
  };

  const handleSaveProfile = (profileData) => {
    updateProfileMutation.mutate(profileData);
  };

  const handleVerifyClick = () => {
    navigate('/venders/protechidchecker');
  };

  const formatPenaltyDueDate = (dueAt) => {
    if (!dueAt) {
      return null;
    }

    try {
      const date = new Date(dueAt);
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return new Intl.DateTimeFormat('ja-JP', {
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      console.warn('Failed to format penalty due date', error);
      return null;
    }
  };

  const getSaleStatusText = (sale) => {
    const penalty = sale.penalty;
    const outstandingAmount = Number(penalty?.outstandingAmount);
    const hasOutstandingPenalty = penalty?.status === 'OUTSTANDING'
      && Number.isFinite(outstandingAmount)
      && outstandingAmount > 0;

    if (hasOutstandingPenalty) {
      const amountLabel = formatCurrency(outstandingAmount);
      const dueLabel = formatPenaltyDueDate(penalty?.dueAt);
      const message = dueLabel
        ? `${dueLabel}までに罰金${amountLabel}をお支払いください`
        : `罰金${amountLabel}をお支払いください`;

      return <span className="text-[#ff2b00]">{message}</span>;
    }

    if (sale.status === 'PAID') {
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: t('sales.messages.shipping_countdown', {
              time: `<span class="text-[#3B5493]">${sale.shippingTimeLeft || '0:00:00'}</span>`
            })
          }}
        />
      );
    }

    return t(getSaleMessageTitleKey(sale.status, sale.listing?.isOfficial));
  };

  return (
    <div className="mx-auto bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <h1 className="text-lg font-bold mb-6">{t('me.profile.info.title')}</h1>
      
      <div className="flex gap-6 mb-6 border-b border-[#f4f4f4] pb-6">
        {/* User Profile */}
        {isLoadingProfile ? (
          <div className="flex-1 flex justify-center items-center">
            <span className="text-gray-400">{t('common.loading')}</span>
          </div>
        ) : (
          <div className="flex-1">
            <div className="inline-flex justify-start items-center gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden">
                <img 
                  src={getAvatarThumbnailUrl(profileInfo.avatar?.url) || avatar} 
                  alt={profileInfo.nickname}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* User Info */}
              <div className="inline-flex flex-col justify-center items-start gap-1">
                <div className="inline-flex justify-end items-center gap-3">
                  <div className="text-[#111111] text-lg font-bold leading-normal">
                    {profileInfo.nickname}
                  </div>
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-full text-xs"
                    onClick={handleOpenProfileEditor}
                  >
                    {t('me.profile.info.edit')}
                  </button>
                </div>
                <div>
                  <span className="text-[#aaaaaa] text-sm font-normal leading-normal">
                    {profileInfo.bio || t('me.profile.info.earnings', { 
                      amount: stats.totalEarnings
                        ? formatCurrency(stats.totalEarnings).replace('¥', '') 
                        : '0' 
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Stats */}
        <div className="grid grid-cols-4">
          <div 
            className="flex flex-col items-center px-6 cursor-pointer hover:bg-gray-50 rounded-md py-2 transition-colors"
            onClick={() => navigate('/me/favorites')}
          >
            <div className="text-lg font-bold mb-1">{stats.favoriteCount}</div>
            <div className="text-sm text-gray-600">{t('me.profile.info.favorites')}</div>
          </div>
          <div 
            className="flex flex-col items-center px-6 cursor-pointer hover:bg-gray-50 rounded-md py-2 transition-colors"
            onClick={() => navigate('/me/listings')}
          >
            <div className="text-lg font-bold mb-1">{stats.listingCount}</div>
            <div className="text-sm text-gray-600">{t('me.profile.info.listings')}</div>
          </div>
          <div 
            className="flex flex-col items-center px-6 cursor-pointer hover:bg-gray-50 rounded-md py-2 transition-colors"
            onClick={() => navigate('/me/sales')}
          >
            <div className="text-lg font-bold mb-1">{stats.soldCount}</div>
            <div className="text-sm text-gray-600">{t('me.profile.info.sold')}</div>
          </div>
          <div 
            className="flex flex-col items-center px-6 cursor-pointer hover:bg-gray-50 rounded-md py-2 transition-colors"
            onClick={() => navigate('/me/orders')}
          >
            <div className="text-lg font-bold mb-1">{stats.purchasedCount}</div>
            <div className="text-sm text-gray-600">{t('me.profile.info.bought')}</div>
          </div>
        </div>
      </div>

      {isBanned && (
        <PenaltyNotice
          className="mt-6"
          variant="banned"
        />
      )}

      {!isBanned && hasOutstandingPenalties && (
        <PenaltyNotice
          className="mt-6"
          variant="outstanding"
          onAction={handlePenaltyAction}
        />
      )}

      {/* Sales Section */}
      <div className="mb-8 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <img src={sellIcon} alt="sell" className="w-5 h-5 mr-2" />
            <h3 className="text-sm font-bold">{t('me.profile.sales.title')}</h3>
          </div>
          {hasSales && (
            <Link to="/me/sales" className="text-sm text-[#2966ff]">
              {t('me.profile.sales.view_all')}
            </Link>
          )}
        </div>

        {isLoadingSales ? (
          <div className="bg-white rounded-lg border border-[#f4f4f4] p-8 text-center">
            <span className="text-gray-400">{t('common.loading')}</span>
          </div>
        ) : hasSales ? (
          <div className="grid grid-cols-2 gap-4">
            {recentSales.map((sale) => (
              <ProfileItemCard
                key={sale.id}
                item={sale.listing}
                statusText={getSaleStatusText(sale)}
                statusLabel={t(getSaleStatusTranslationKey(sale.status, sale.listing?.isOfficial))}
                routePrefix="/listing"
                saleId={sale.id}
                onClick={() => navigate(`/me/sales`)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#f4f4f4] p-8 text-center">
            <p className="text-sm text-[#666666]">{t('user.no_records.sales')}</p>
          </div>
        )}
      </div>

      {/* Purchases Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <img src={orderIcon} alt="order" className="w-5 h-5 mr-2" />
            <h3 className="text-sm font-bold">{t('me.profile.purchases.title')}</h3>
          </div>
          {hasPurchases && (
            <Link to="/me/orders" className="text-sm text-[#2966ff]">
              {t('me.profile.purchases.view_all')}
            </Link>
          )}
        </div>

        {isLoadingPurchases ? (
          <div className="bg-white rounded-lg border border-[#f4f4f4] p-8 text-center">
            <span className="text-gray-400">{t('common.loading')}</span>
          </div>
        ) : hasPurchases ? (
          <div className="grid grid-cols-2 gap-4">
            {recentPurchases.map((purchase) => (
              <ProfileItemCard
                key={purchase.id}
                item={purchase.listing}
                statusText={t(getOrderMessageTitleKey(purchase.status, purchase.listing.isOfficial))}
                statusLabel={t(getOrderStatusTranslationKey(purchase.status, purchase.listing.isOfficial))}
                orderId={purchase.id}
                onClick={() => navigate(`/me/orders`)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#f4f4f4] p-8 text-center">
            <p className="text-sm text-[#666666]">{t('user.no_records.purchases')}</p>
          </div>
        )}
      </div>

      {/* Collections Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <img src={favIcon} alt="fav" className="w-5 h-5 mr-2" />
            <h3 className="text-sm font-bold">{t('me.profile.favorites.title')}</h3>
          </div>
          {hasFavorites && (
            <Link to="/me/favorites" className="text-sm text-[#2966ff]">
              {t('me.profile.favorites.view_all')}
            </Link>
          )}
        </div>

        {isLoadingFavorites ? (
          <div className="bg-white rounded-lg border border-[#f4f4f4] p-8 text-center">
            <span className="text-gray-400">{t('common.loading')}</span>
          </div>
        ) : hasFavorites ? (
          <div className="grid grid-cols-4 gap-4 overflow-x-auto pb-2">
            {recentFavorites.map((watch) => {
              const { watchable } = watch;
              if (!watchable) return null;
              
              return (
                <CardItemView
                  key={watchable.sourceId}
                  id={watch.productId}
                  type={watch.type}
                  price={watchable.currentPrice || 0}
                  image={watchable.image?.url}
                  name={watchable.localizedRawName || watchable.localizedName}
                  brand={watchable.brand?.localizedName}
                  code={watchable.code}
                  rawCode={watchable.rawCode}
                  set={watchable.set}
                  rarityInfo={watchable.rarityInfo}
                  customRightText={getWatchTypeDisplay(watch, t)}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#f4f4f4] p-8 text-center">
            <p className="text-sm text-[#666666]">{t('user.no_records.favorites')}</p>
          </div>
        )}
      </div>

      {/* Profile Editor Modal */}
      {isProfileEditorOpen && (
        <ProfileEditor
          isOpen={true}
          onClose={handleCloseProfileEditor}
          onSave={handleSaveProfile}
          profileData={profileData?.data}
        />
      )}
    </div>
  );
};

export default ProfileInfoPage; 
